import {
    // ChatBubbleOutlineRounded,
    // DeleteOutlineRounded,
    DescriptionOutlined,
    LayersOutlined,
    Notes,
    NotificationsActiveOutlined,
    Person2Outlined,
    PersonOutlineRounded,
} from '@mui/icons-material';
import { Badge, Box, Popover, ThemeProvider } from '@mui/material';
import { useEffect, useMemo, useState, type ElementType } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import ConfirmationDialog from 'components/v2/common/confirmationDialog';
import ActionButton from 'views/Dashboards-B2B/components/Button';
import FooterButton from 'views/Dashboards-B2B/components/FooterButton';
import * as QuickActionStyles from 'views/Dashboards-B2B/components/quick-actions/QuickActions.styled';
import CalendarReminderPage from 'views/v2/calendarReminder/containers';

import api, { ClientServiceProvider } from 'api';
import { ClientApplicationStatus } from 'api/models/ClientApplicationStatus';
import { ACTIONS } from 'constant';
import useModalObj from 'hooks/useModalObj';
import useTranslate from 'hooks/useTranslate';
import { ReactComponent as Lightning } from 'assets/icons/B2B/bolt.svg';
import { setAlert } from 'redux/alert';
import { loadSalesClients } from 'redux/sales-clients/sales-clients.middleware';
import type { AppDispatch, RootState } from 'redux/store';
import appThemeV2 from 'theme/themeV2';

type ActionItem = {
    key:
        | 'profile'
        | 'documents'
        | 'chats'
        | 'reminder'
        | 'remove'
        | 'agents'
        | 'agreement'
        | 'templates';
    label: string;
    icon: ElementType;
    color?: 'primary' | 'error';
    disabled?: boolean;
    showUnreadDot?: boolean;
    onClick: () => void;
};

interface OrganizationQuickActionProps {
    variant?: 'card' | 'footer';
}

const OrganizationQuickAction = ({ variant = 'card' }: OrganizationQuickActionProps) => {
    const dispatch = useDispatch<AppDispatch>();
    const navigate = useNavigate();
    const location = useLocation();
    const { id } = useParams();
    const activeAppId = useSelector((state: RootState) => state.application.entity?.id);

    const [activeCompany, setActiveCompany] = useState<ClientServiceProvider | null>(null);
    const [hasUnreadMessages, setHasUnreadMessages] = useState(false);

    const { modalObject, setModalObject, onResetModalObj } = useModalObj();
    const { translate } = useTranslate();

    const currentSection = useMemo(() => {
        if (location.pathname.endsWith('/documents')) {
            return 'documents';
        }

        if (location.pathname.endsWith('/agreement')) {
            return 'agreement';
        }

        if (location.pathname.endsWith('/chats')) {
            return 'chats';
        }

        if (location.pathname.endsWith('/agents')) {
            return 'agents';
        }

        return 'profile';
    }, [location.pathname]);

    const handleRemove = async () => {
        if (!activeCompany?.id) {
            return;
        }

        try {
            setModalObject((p) => ({ ...p, disabled: true }));

            await api.application.setClientApplicationStatus(activeCompany.id.toString(), {
                client_status: ClientApplicationStatus.REMOVED,
            });
            dispatch(loadSalesClients({}));
            onResetModalObj();
            navigate('/dashboard');
        } catch {
            setModalObject((p) => ({ ...p, disabled: false }));
        }
    };

    const handleAction = (action: string, data?: unknown) => {
        switch (action) {
            case 'CHATS': {
                setHasUnreadMessages(false);
                navigate(`/dashboard/${id}/organization/chats`);
                break;
            }
            case 'PROFILE': {
                navigate(`/dashboard/${id}/organization`);
                break;
            }
            case 'DOCUMENTS': {
                navigate(`/dashboard/${id}/organization/documents`);
                break;
            }
            case 'AGREEMENT': {
                navigate(`/dashboard/${id}/organization/agreement`);
                break;
            }
            case 'REMAINDER': {
                setModalObject((p) => ({
                    ...p,
                    action,
                    title: translate('addReminder'),
                    data: data,
                }));
                break;
            }
            case 'AGENTS': {
                navigate(`/dashboard/${id}/organization/agents`);
                break;
            }
            case 'TEMPLATES': {
                navigate('/templates');
                break;
            }
            case ACTIONS.DELETE:
                setModalObject((p) => ({
                    ...p,
                    action,
                    title: translate('removeCase'),
                    content: `${translate('areYouSureDeleteApp')} ${translate(
                        'thisCannotBeUndone'
                    )}!`,
                    data: data,
                }));
                break;

            default:
                break;
        }
    };

    const actions = useMemo<ActionItem[]>(
        () => [
            {
                key: 'profile',
                label: translate('clientProfile'),
                icon: PersonOutlineRounded,
                onClick: () => handleAction('PROFILE', null),
            },
            {
                key: 'documents',
                label: translate('documents'),
                icon: DescriptionOutlined,
                disabled: !activeCompany?.id,
                onClick: () => handleAction('DOCUMENTS', null),
            },
            {
                key: 'agents',
                label: translate('agents'),
                icon: Person2Outlined,
                disabled: !activeCompany?.id,
                onClick: () => handleAction('AGENTS'),
            },
            // {
            //     key: 'chats',
            //     label: translate('chats'),
            //     icon: ChatBubbleOutlineRounded,
            //     disabled: !activeCompany?.id,
            //     showUnreadDot: hasUnreadMessages,
            //     onClick: () => handleAction('CHATS'),
            // },
            {
                key: 'agreement',
                label: translate('agreement'),
                icon: Notes,
                disabled: !activeCompany?.id,
                onClick: () => handleAction('AGREEMENT'),
            },
            {
                key: 'templates',
                label: translate('manageTemplates'),
                icon: LayersOutlined,
                onClick: () => handleAction('TEMPLATES'),
            },
            {
                key: 'reminder',
                label: translate('addReminder'),
                icon: NotificationsActiveOutlined,
                disabled: !activeCompany?.id,
                onClick: () => handleAction('REMAINDER', activeCompany?.id),
            },
            // {
            //     key: 'remove',
            //     label: translate('remove'),
            //     icon: DeleteOutlineRounded,
            //     color: 'error',
            //     disabled: !activeCompany?.id,
            //     onClick: () => handleAction(ACTIONS.DELETE, activeCompany?.id),
            // },
        ],
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [activeCompany?.id, hasUnreadMessages, translate]
    );

    const getById = async (id: string) => {
        try {
            const response = await api.clientServiceProviders.getClientServiceProviderById(id);
            if (response.data) {
                setActiveCompany(response.data);
            }
        } catch {
            dispatch(setAlert({ message: translate('somethingWentWrong'), type: 'error' }));
        }
    };

    useEffect(() => {
        if (id) {
            getById(id);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    useEffect(() => {
        const fetchUnreadChats = async () => {
            if (!activeCompany?.id) {
                setHasUnreadMessages(false);
                return;
            }

            try {
                const response = await api.chatNew.has_unread_messages(activeCompany.id.toString());

                if (response.status === 200) {
                    setHasUnreadMessages(response.data?.has_unread_messages || false);
                }
            } catch (error: unknown) {
                const responseStatus =
                    typeof error === 'object' &&
                    error !== null &&
                    'response' in error &&
                    typeof error.response === 'object' &&
                    error.response !== null &&
                    'status' in error.response
                        ? error.response.status
                        : undefined;

                if (responseStatus !== 403) {
                    console.error(error);
                }
            }
        };

        fetchUnreadChats();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeCompany?.id]);

    const [footerOpen, setFooterOpen] = useState(false);

    const card = (
        <QuickActionStyles.ActionsContainer
            style={{
                width: variant === 'footer' ? 'min(420px, calc(100vw - 32px))' : '100%',
                marginBottom: 0,
            }}
        >
            <QuickActionStyles.TitleWrapper>
                <QuickActionStyles.Eyebrow>{translate('quickActions')}</QuickActionStyles.Eyebrow>
            </QuickActionStyles.TitleWrapper>
            <QuickActionStyles.ActionsButtonContainer>
                {actions.map((action) => {
                    const Icon = action.icon;
                    const isActive = currentSection === action.key;
                    const isDestructive = action.color === 'error';
                    const color = isDestructive ? 'red' : 'purple';

                    return (
                        <ActionButton
                            key={action.key}
                            isActive={isActive}
                            color={color}
                            border={color}
                            disabled={action.disabled}
                            aria-current={isActive ? 'page' : undefined}
                            onClick={() => {
                                action.onClick();
                                setFooterOpen(false);
                            }}
                            style={{ width: '100%' }}
                        >
                            {action.showUnreadDot ? (
                                <Badge
                                    color="error"
                                    overlap="circular"
                                    variant="dot"
                                    anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                                >
                                    <Icon sx={{ fontSize: 16 }} />
                                </Badge>
                            ) : (
                                <Icon sx={{ fontSize: 16 }} />
                            )}
                            {action.label}
                        </ActionButton>
                    );
                })}
            </QuickActionStyles.ActionsButtonContainer>
        </QuickActionStyles.ActionsContainer>
    );

    const dialogs = (
        <>
            {modalObject.action === 'DELETE' && (
                <ConfirmationDialog
                    title={modalObject.title}
                    content={modalObject.content}
                    onClose={onResetModalObj}
                    onConfirm={handleRemove}
                />
            )}

            {modalObject.action === 'REMAINDER' && (
                <CalendarReminderPage
                    onClose={onResetModalObj}
                    appId={activeAppId != null ? String(activeAppId) : ''}
                />
            )}
        </>
    );

    return (
        <ThemeProvider theme={appThemeV2}>
            {variant === 'footer' ? (
                <>
                    <FooterButton
                        color="purple"
                        isActive={footerOpen}
                        onClick={() => setFooterOpen((open) => !open)}
                    >
                        <Lightning style={{ transform: 'scale(1.6)', paddingRight: '5px' }} />
                        {translate('quickActions')}
                    </FooterButton>
                    <Popover
                        open={footerOpen}
                        onClose={() => setFooterOpen(false)}
                        anchorReference="none"
                        marginThreshold={0}
                        sx={{ zIndex: 9100 }}
                        slotProps={{
                            paper: {
                                sx: {
                                    backgroundColor: 'transparent',
                                    top: 'auto !important',
                                    bottom: '64px',
                                    left: '0 !important',
                                    right: 0,
                                    width: '100%',
                                    maxWidth: 'none',
                                    maxHeight: 'calc(100vh - 88px)',
                                    overflowY: 'auto',
                                    transform: 'none !important',
                                    boxShadow: 'none',
                                },
                            },
                        }}
                    >
                        <Box sx={{ display: 'flex', justifyContent: 'center', px: 2, py: 1 }}>
                            {card}
                        </Box>
                    </Popover>
                </>
            ) : (
                card
            )}
            {dialogs}
        </ThemeProvider>
    );
};

export default OrganizationQuickAction;
